import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowLeft,
  Download,
  FileText,
  Loader2,
  Plus,
  Save,
  Trash2,
  User,
  Building,
  MapPin,
  ChevronDown
} from 'lucide-react';
import PermissionGate from '../../../components/common/PermissionGate';
import { API_BASE_URL, API_ENDPOINTS } from '../../../api/config';
import { del, get, post } from '../../../api/apiClient';
import doiService from '../../../api/crm/doi/doiService';
import opportunityService from '../../../api/crm/opportunities/opportunityService';
import customerService from '../../../api/crm/customers/customerService';
import leadService from '../../../api/crm/leads/leadService';
import teamManagementService from '../../../api/crm/team-management/teamManagementService';
import {
  sanitizeDescriptionInput,
  sanitizeEmailInput,
  validateEmailInput,
  validatePhoneInput
} from '../../../utils/inputValidation';
import { toast } from '../../../utils/toast';

const PROJECT_TYPE_OPTIONS = ['FB', 'Tracker', 'ODC', 'Monthly', 'Quarterly'];

const isValidObjectId = (value) => typeof value === 'string' && /^[a-f\d]{24}$/i.test(value.trim());
const PROJECT_START_STAGE_OPTIONS = ['WF', 'Design', 'Development'];
const CODE_BASE_OPTIONS = ['Scratch', 'Existing'];
const WEBSITE_WEB_SERVICES_OPTIONS = ['Provided by Client', 'Mobiloitte', 'Client + Mobiloitte'];
const BASE_TIMEZONE_OPTIONS = typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function'
  ? Intl.supportedValuesOf('timeZone')
  : [];
const TIMEZONE_OPTIONS = Array.from(new Set([
  'Asia/Kolkata',
  ...BASE_TIMEZONE_OPTIONS,
  ...(!BASE_TIMEZONE_OPTIONS.length ? [
    'Asia/Kolkata',
    'Europe/London',
    'America/New_York',
    'America/Chicago',
    'America/Los_Angeles',
    'Europe/Berlin',
    'Asia/Dubai',
    'Asia/Singapore',
    'Australia/Sydney'
  ] : [])
]));
const TIMEZONE_ABBREVIATIONS = {
  'Asia/Kolkata': 'IST',
  'Europe/London': 'GMT/BST',
  'America/New_York': 'ET',
  'America/Chicago': 'CT',
  'America/Denver': 'MT',
  'America/Los_Angeles': 'PT',
  'Asia/Dubai': 'GST',
  'Asia/Singapore': 'SGT',
  'Australia/Sydney': 'AET'
};
const MAX_FIELD_LENGTH = 250;
const MAX_TEXTAREA_LENGTH = 2000;

const DOI_FIELDS = [
  { key: 'leadId', label: 'Lead ID' },
  { key: 'leadDate', label: 'Lead Date', type: 'date' },
  { key: 'leadGenerator', label: 'Lead Generator' },
  { key: 'salesRep', label: 'Sales Rep' },
  { key: 'leadSource', label: 'Lead Source' },
  { key: 'doiDate', label: 'DOI Date', type: 'date' },
  { key: 'projectName', label: 'Project Name' },
  { key: 'projectType', label: 'Project Type (FB/Tracker/ODC/Monthly)', control: 'projectType' },
  { key: 'platformDomain1', label: 'Platform/Domain 1' },
  { key: 'platformDomain2', label: 'Platform/Domain 2' },
  { key: 'platformDomain3', label: 'Platform/Domain 3' },
  { key: 'platformDomain4', label: 'Platform/Domain 4' },
  { key: 'platformDomain5', label: 'Platform/Domain 5' },
  { key: 'websiteWebServices', label: 'Website/web services (Provided by Client / Mobiloitte)', control: 'websiteWebServices' },
  { key: 'projectStartStage', label: 'Project Start Stage (WF / Design / Development)', control: 'projectStartStage' },
  { key: 'codeBase', label: 'Code base (Scratch/Existing)', control: 'codeBase' },
  { key: 'clientLocation', label: 'Client Location' },
  { key: 'timeZone', label: 'Time Zone' },
  { key: 'mainCustomerNames', label: 'Main Customer Name(s)' },
  { key: 'customerEmail', label: 'Customer Email id', type: 'email' },
  { key: 'customerSkypeId', label: 'Customer Skype ID' },
  { key: 'customerWhatsappId', label: 'Customer Whatsapp ID#' },
  { key: 'projectOrderValueRate', label: 'Project Order Value/Rate (Including Taxes)' },
  { key: 'projectWebsite', label: 'Project Website' },
  { key: 'estimationManHours', label: 'Estimation # of Man Hours' },
  { key: 'estimationApprovedBy', label: 'Estimation done/approved by', control: 'estimationApprovedBy' },
  { key: 'domainWiseValueDistribution', label: 'Domain wise distribution of value (Excluding Taxes)' },
  { key: 'advanceAmountReceived', label: 'Advance amount Received' },
  { key: 'totalTimelineManDays', label: 'Total time line (Man days)' },
  { key: 'sow', label: 'SOW' },
  { key: 'detailedEstimation', label: 'Detailed Estimation', multiline: true },
  { key: 'projectRequirementOverview', label: 'Overview of Project/Business Requirement', multiline: true },
  { key: 'risksAndAssumptions', label: 'Risks and Assumptions (Please specify)', multiline: true },
  { key: 'finalDeliveryDateCommitted', label: 'Any Final Date of Delivery Committed', type: 'date' },
  { key: 'milestonesSprintsDeliveryDate', label: 'Milestones / Sprints with Date of Delivery', multiline: true }
];

const REQUIRED_DOI_FIELDS = new Set(['leadId']);
const isRequiredDoiField = (fieldKey) => REQUIRED_DOI_FIELDS.has(fieldKey);
const NUMERIC_ONLY_DOI_FIELDS = new Set(['estimationManHours']);
const isNumericOnlyDoiField = (fieldKey) => NUMERIC_ONLY_DOI_FIELDS.has(fieldKey);
const sanitizeNumericInput = (value) => String(value || '').replace(/\D/g, '');

const FIELD_PLACEHOLDERS = {
  leadId: 'Auto-filled from selected lead, or enter lead reference',
  leadDate: 'Select lead date',
  leadGenerator: 'Auto-filled from lead creator',
  salesRep: 'Select assigned sales owner',
  leadSource: 'Select lead source',
  doiDate: 'Select DOI date',
  projectName: 'Enter project or opportunity name',
  projectType: 'Select project type',
  platformDomain1: 'Enter platform/domain',
  platformDomain2: 'Enter platform/domain',
  platformDomain3: 'Enter platform/domain',
  platformDomain4: 'Enter platform/domain',
  platformDomain5: 'Enter platform/domain',
  websiteWebServices: 'Select service provider',
  projectStartStage: 'Select project start stage',
  codeBase: 'Select code base',
  clientLocation: 'Auto-filled from lead country, or enter location',
  timeZone: 'Enter client time zone, e.g. UK, IST, EST',
  mainCustomerNames: 'Enter primary customer name(s)',
  customerEmail: 'Enter customer email',
  customerSkypeId: 'Enter Skype ID',
  customerWhatsappId: 'Enter WhatsApp number',
  projectOrderValueRate: 'Enter order value/rate, e.g. 1,50,000',
  projectWebsite: 'Enter project website URL',
  estimationManHours: 'Enter estimated hours',
  estimationApprovedBy: 'Select approver(s)',
  domainWiseValueDistribution: 'Enter value split by domain',
  advanceAmountReceived: 'Enter advance amount received',
  totalTimelineManDays: 'Enter timeline, e.g. 7 days',
  detailedEstimation: 'Summarize estimation details',
  projectRequirementOverview: 'Summarize business/project requirement',
  sow: 'Enter SOW status/details',
  risksAndAssumptions: 'Mention risks, assumptions, and dependencies',
  finalDeliveryDateCommitted: 'Select committed delivery date',
  milestonesSprintsDeliveryDate: 'Enter milestones/sprints with delivery dates'
};

const emptyDoiFields = DOI_FIELDS.reduce((acc, field) => {
  acc[field.key] = '';
  return acc;
}, {});

const toDateInputValue = (value) => {
  if (!value) return '';
  const text = String(value).trim();
  const slashDateMatch = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
  const normalizedValue = slashDateMatch
    ? `${slashDateMatch[3].length === 2 ? `20${slashDateMatch[3]}` : slashDateMatch[3]}-${slashDateMatch[2].padStart(2, '0')}-${slashDateMatch[1].padStart(2, '0')}`
    : value;
  const date = new Date(normalizedValue);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};

const joinName = (...parts) => parts.filter(Boolean).join(' ').trim();

const isNA = (value) => String(value || '').trim().toLowerCase() === 'n/a';

const getUserName = (user) => (
  (typeof user === 'string' ? user : '') ||
  user?.name ||
  user?.fullName ||
  joinName(user?.firstName, user?.lastName) ||
  user?.email ||
  ''
);

const getEntityId = (value) => (
  typeof value === 'object' && value !== null
    ? value._id || value.id || ''
    : value || ''
);

const getSalesRepUserId = (user) => {
  if (!user) return '';
  if (typeof user === 'string') return user;
  if (user.user || user.userId) return String(user.user || user.userId);
  return String(user._id || user.id || '');
};

const isSameSalesRep = (user, selectedId) => {
  if (!selectedId) return false;
  const selected = String(selectedId);
  const ids = [user?.user, user?.userId, user?._id, user?.id].filter(Boolean).map(String);
  return ids.includes(selected);
};

const resolveSalesRepFromDoi = (salesRepUser) => {
  if (!salesRepUser) return { userId: '', displayName: '' };
  if (typeof salesRepUser === 'string') {
    return { userId: salesRepUser, displayName: '' };
  }
  return {
    userId: String(salesRepUser.user || salesRepUser._id || salesRepUser.id || ''),
    displayName: getUserName(salesRepUser)
  };
};

const getLinkedCustomerId = (opportunity, customerIdFromQuery) => (
  customerIdFromQuery
  || getEntityId(opportunity?.customerId)
  || getEntityId(opportunity?.customer)
  || ''
);

const formatMilestonesForField = (milestones = []) => {
  if (typeof milestones === 'string') return milestones;
  if (!Array.isArray(milestones)) return '';
  return milestones
    .map((milestone) => {
      if (typeof milestone === 'string') return milestone.trim();
      const name = String(milestone?.name || milestone?.title || milestone?.milestone || milestone?.sprint || milestone?.description || '').trim();
      const deliveryDate = toDateInputValue(
        milestone?.deliveryDate ||
        milestone?.dateOfDelivery ||
        milestone?.date ||
        milestone?.dueDate
      );
      if (!name && !deliveryDate) return '';
      return deliveryDate ? `${name}: ${deliveryDate}` : name;
    })
    .filter(Boolean)
    .join('\n');
};

const parseMilestonesFromField = (value = '') => String(value || '')
  .split(/\n|;/)
  .map((entry) => entry.trim())
  .filter(Boolean)
  .map((entry) => {
    const dateMatch = entry.match(/\b\d{4}-\d{2}-\d{2}\b|\b\d{1,2}[/-]\d{1,2}[/-]\d{2,4}\b/);
    const deliveryDate = dateMatch ? toDateInputValue(dateMatch[0]) : null;
    const name = entry
      .replace(/\b\d{4}-\d{2}-\d{2}\b|\b\d{1,2}[/-]\d{1,2}[/-]\d{2,4}\b/, '')
      .replace(/[:|-]\s*$/, '')
      .trim();

    return {
      name: name || entry,
      deliveryDate
    };
  });

const isObjectIdLike = (value) => /^[a-f\d]{24}$/i.test(String(value || '').trim());

const getBestUserName = (...users) => {
  const names = users.map(getUserName).filter(Boolean);
  return names.find((name) => !isObjectIdLike(name)) || names[0] || '';
};

const normalizeSourceValue = (source) => {
  if (!source) return '';
  if (typeof source === 'object') {
    return source.name || source.label || source.id || source._id || '';
  }
  return String(source);
};

const firstAvailable = (...values) => (
  values.find((value) => {
    if (value === undefined || value === null) return false;
    return String(value).trim() !== '';
  }) ?? ''
);

const normalizeSearchText = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');

const getTimeZoneOffsetLabel = (timeZone) => {
  try {
    const part = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'shortOffset'
    }).formatToParts(new Date()).find((item) => item.type === 'timeZoneName');

    return part?.value ? part.value.replace('GMT', 'UTC') : '';
  } catch {
    return '';
  }
};

const getTimeZoneDisplayLabel = (timeZone) => {
  if (!timeZone) return '';
  const abbreviation = TIMEZONE_ABBREVIATIONS[timeZone] || '';
  const offset = getTimeZoneOffsetLabel(timeZone);
  const details = [abbreviation, offset].filter(Boolean).join(', ');
  return details ? `${timeZone} (${details})` : timeZone;
};

const getTimeZoneSearchValues = (timeZone) => {
  const parts = String(timeZone || '').split(/[\/_ -]+/).filter(Boolean);
  return [
    timeZone,
    getTimeZoneDisplayLabel(timeZone),
    TIMEZONE_ABBREVIATIONS[timeZone],
    getTimeZoneOffsetLabel(timeZone),
    timeZone.replace(/_/g, ' '),
    timeZone.replace(/[\/_]+/g, ' '),
    parts.join(' '),
    parts.reverse().join(' ')
  ].map(normalizeSearchText);
};

const getPrimaryPhone = (customer, lead) => {
  if (Array.isArray(customer?.phone) && customer.phone[0]) return customer.phone[0];
  return firstAvailable(
    customer?.phone,
    lead?.whatsappPhone,
    lead?.phone,
    lead?.alternatePhone
  );
};

const getCountryName = (lead, customer) => {
  const leadLocation = Array.isArray(lead?.locations) ? lead.locations[0] : null;
  const leadCountry = leadLocation?.country;
  const customerCountry = customer?.billingAddress?.country || customer?.shippingAddress?.country;
  const country = firstAvailable(
    typeof leadCountry === 'object' ? leadCountry?.name || leadCountry?.isoCode || leadCountry?.iso2 : leadCountry,
    lead?.address?.country,
    typeof customerCountry === 'object' ? customerCountry?.name || customerCountry?.isoCode || customerCountry?.iso2 : customerCountry
  );

  return country;
};

const cleanLocationValue = (value) => {
  const parts = String(value || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  const cleaned = [];
  const seen = new Set();

  parts.forEach((part) => {
    const normalized = part.toLowerCase().replace(/\s+/g, ' ');
    const compact = normalized.replace(/[^a-z0-9]/g, '');
    const isPostalOnly = /^\d{4,10}$/.test(compact);
    const alreadyIncluded = cleaned.some((existing) => {
      const existingNormalized = existing.toLowerCase().replace(/\s+/g, ' ');
      const existingCompact = existingNormalized.replace(/[^a-z0-9]/g, '');
      return existingCompact === compact ||
        (isPostalOnly && existingCompact.includes(compact)) ||
        (compact.length > 2 && existingCompact.startsWith(compact) && /\d/.test(existingCompact));
    });

    if (!seen.has(compact) && !alreadyIncluded) {
      seen.add(compact);
      cleaned.push(part);
    }
  });

  return cleaned.join(', ');
};

const getLeadLocationValue = (lead, customer) => {
  const leadLocation = Array.isArray(lead?.locations) ? lead.locations[0] : null;
  const address = leadLocation?.address;
  const country = getCountryName(lead, customer);
  return cleanLocationValue(firstAvailable(
    customer?.billingAddress?.fullAddress,
    customer?.shippingAddress?.fullAddress,
    address?.fullAddress,
    lead?.address?.street,
    address?.street,
    lead?.address?.city,
    address?.city?.name || address?.city,
    lead?.address?.state,
    address?.state?.name || address?.state,
    country,
    lead?.address?.pincode,
    address?.postalCode,
    customer?.billingAddress?.postalCode,
    customer?.shippingAddress?.postalCode
  ));
};

const formatLeadValue = (value) => {
  if (value === undefined || value === null || value === '') return '';
  return String(value);
};

const formatProjectValue = (value) => {
  const formattedValue = formatLeadValue(value);
  if (!formattedValue) return '';
  const numericText = String(formattedValue).replace(/[^\d.]/g, '');
  if (!numericText) return formattedValue;
  const numericValue = Number(numericText);
  if (!Number.isFinite(numericValue)) return formattedValue;
  return `₹${numericValue.toLocaleString('en-IN', {
    maximumFractionDigits: 2,
    minimumFractionDigits: formattedValue.includes('.') ? 2 : 0
  })}`;
};

const formatProjectValueInput = (value) => {
  const rawValue = String(value || '');
  const numericText = rawValue.replace(/[^\d.]/g, '');
  if (!numericText) return '';
  const [integerPart, decimalPart] = numericText.split('.');
  const formattedInteger = Number(integerPart || 0).toLocaleString('en-IN');
  return decimalPart !== undefined
    ? `₹${formattedInteger}.${decimalPart.slice(0, 2)}`
    : `₹${formattedInteger}`;
};

const isValidUrlOrNA = (value) => {
  const trimmed = String(value || '').trim();
  if (!trimmed || isNA(trimmed)) return true;
  try {
    const url = new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`);
    return Boolean(url.hostname && url.hostname.includes('.'));
  } catch {
    return false;
  }
};

const QuoteBuilder = () => {
  const navigate = useNavigate();
  const { opportunityId } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const opportunityIdFromQuery = searchParams.get('opportunityId');
  const customerIdFromQuery = searchParams.get('customerId');
  const doiIdFromQuery = searchParams.get('doiId');
  const finalOpportunityId = opportunityId || opportunityIdFromQuery;
  const isLeadIdLocked = Boolean(finalOpportunityId || customerIdFromQuery || doiIdFromQuery);

  const [doiFields, setDoiFields] = useState(emptyDoiFields);
  const [errors, setErrors] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [leadSources, setLeadSources] = useState([]);
  const [selectedEstimatorIds, setSelectedEstimatorIds] = useState([]);
  const [estimatorSearch, setEstimatorSearch] = useState('');
  const [isEstimatorDropdownOpen, setIsEstimatorDropdownOpen] = useState(false);
  const [selectedSalesRepId, setSelectedSalesRepId] = useState('');
  const [salesRepSearch, setSalesRepSearch] = useState('');
  const [isSalesRepDropdownOpen, setIsSalesRepDropdownOpen] = useState(false);
  const [locationPredictions, setLocationPredictions] = useState([]);
  const [showLocationPredictions, setShowLocationPredictions] = useState(false);
  const [timeZoneSearch, setTimeZoneSearch] = useState('');
  const [isTimeZoneDropdownOpen, setIsTimeZoneDropdownOpen] = useState(false);
  const estimatorDropdownRef = useRef(null);
  const salesRepDropdownRef = useRef(null);
  const locationDropdownRef = useRef(null);
  const timeZoneDropdownRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const locationRequestSeqRef = useRef(0);

  const {
    data: doiData,
    isLoading: isLoadingDoi,
    isError: isErrorDoi,
    error: doiError
  } = useQuery({
    queryKey: ['doi', doiIdFromQuery],
    queryFn: async () => {
      const result = await doiService.getDoiById(doiIdFromQuery);
      if (!result.success) throw new Error(result.error || 'Failed to load DOI data.');
      return result.doi;
    },
    enabled: !!doiIdFromQuery,
    retry: 1
  });

  const {
    data: opportunity,
    isLoading: isLoadingOpportunity,
    isError: isErrorOpportunity,
    error: opportunityError,
    refetch: refetchOpportunity
  } = useQuery({
    queryKey: ['opportunity', finalOpportunityId],
    queryFn: async () => {
      const result = await opportunityService.getOpportunityById(finalOpportunityId);
      if (!result.success) throw new Error(result.error || 'Failed to load opportunity data.');
      return result.opportunity;
    },
    enabled: !!finalOpportunityId && !doiIdFromQuery && !customerIdFromQuery,
    retry: 1
  });

  const leadIdForQuery = opportunity?.leadId?._id || (typeof opportunity?.leadId === 'string' ? opportunity.leadId : null);
  const linkedCustomerId = getLinkedCustomerId(opportunity, customerIdFromQuery);
  const {
    data: leadData,
    isLoading: isLoadingLead
  } = useQuery({
    queryKey: ['lead', leadIdForQuery],
    queryFn: async () => {
      const result = await leadService.getLeadById(leadIdForQuery);
      if (!result.success) throw new Error(result.error || 'Failed to load lead data.');
      return result.lead;
    },
    enabled: !!leadIdForQuery && !doiIdFromQuery && !customerIdFromQuery,
    retry: 1
  });

  const {
    data: customer,
    isLoading: isLoadingCustomer,
    isError: isErrorCustomer,
    error: customerError,
    refetch: refetchCustomer
  } = useQuery({
    queryKey: ['customer', linkedCustomerId],
    queryFn: async () => {
      const result = await customerService.getCustomerById(linkedCustomerId);
      if (!result.success) throw new Error(result.error || 'Failed to load customer data.');
      return result.customer;
    },
    enabled: !!linkedCustomerId && !doiIdFromQuery,
    retry: 1
  });

  const {
    data: crmUsersData,
    isLoading: isLoadingCrmUsers
  } = useQuery({
    queryKey: ['crmUsers', 'doi-estimators', estimatorSearch],
    queryFn: async () => {
      const result = await teamManagementService.getCRMUsers({
        limit: estimatorSearch && estimatorSearch.length >= 2 ? 100 : 50,
        ...(estimatorSearch && estimatorSearch.length >= 2 ? { search: estimatorSearch } : {})
      });
      if (!result.success) throw new Error(result.error || 'Failed to load CRM users.');
      return result.users;
    },
    retry: 1
  });

  const crmUsers = crmUsersData || [];

  const {
    data: salesRepUsersData,
    isLoading: isLoadingSalesRepUsers
  } = useQuery({
    queryKey: ['crmUsers', 'doi-sales-reps', salesRepSearch],
    queryFn: async () => {
      const result = await teamManagementService.getCRMUsers({
        limit: salesRepSearch && salesRepSearch.length >= 2 ? 100 : 50,
        ...(salesRepSearch && salesRepSearch.length >= 2 ? { search: salesRepSearch } : {})
      });
      if (!result.success) throw new Error(result.error || 'Failed to load sales reps.');
      return result.users;
    },
    retry: 1
  });

  const salesRepUsers = useMemo(() => {
    const users = salesRepUsersData || [];
    if (!doiData?.salesRepUserId) return users;

    const { userId, displayName } = resolveSalesRepFromDoi(doiData.salesRepUserId);
    if (!userId) return users;
    if (users.some((user) => isSameSalesRep(user, userId))) return users;

    return [
      {
        _id: userId,
        id: userId,
        user: userId,
        name: displayName,
        email: doiData.salesRepUserId?.email || ''
      },
      ...users
    ];
  }, [doiData?.salesRepUserId, salesRepUsersData]);

  useEffect(() => {
    const fetchSources = async () => {
      try {
        const result = await get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_SOURCES}`);
        const sources = Array.isArray(result?.data)
          ? result.data
          : Array.isArray(result?.data?.data)
            ? result.data.data
            : Array.isArray(result?.leadSources)
              ? result.leadSources
              : [];

        if (result?.success || result?.responseCode === 200 || sources.length > 0) {
          setLeadSources(sources);
        } else {
          setLeadSources([]);
          toast.error(result?.responseMessage || result?.message || 'Failed to load lead sources');
        }
      } catch (error) {
        toast.error('Failed to load lead sources');
      }
    };

    fetchSources();
  }, []);

  useEffect(() => {
    const currentSource = String(doiFields.leadSource || '').trim();
    if (!currentSource || currentSource === 'N/A' || leadSources.length === 0) return;

    const matchedSource = leadSources.find((source) => (
      String(source._id) === currentSource ||
      String(source.id) === currentSource ||
      String(source.name).toLowerCase() === currentSource.toLowerCase()
    ));

    if (matchedSource?.name && matchedSource.name !== currentSource) {
      setDoiFields((prev) => ({ ...prev, leadSource: matchedSource.name }));
    }
  }, [doiFields.leadSource, leadSources]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (estimatorDropdownRef.current && !estimatorDropdownRef.current.contains(event.target)) {
        setIsEstimatorDropdownOpen(false);
      }
      if (salesRepDropdownRef.current && !salesRepDropdownRef.current.contains(event.target)) {
        setIsSalesRepDropdownOpen(false);
      }
      if (locationDropdownRef.current && !locationDropdownRef.current.contains(event.target)) {
        setShowLocationPredictions(false);
      }
      if (timeZoneDropdownRef.current && !timeZoneDropdownRef.current.contains(event.target)) {
        setIsTimeZoneDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.google?.maps?.places && !autocompleteServiceRef.current) {
      autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
    }
  }, []);

  useEffect(() => {
    if (!doiData) return;

    // Format monetary value with currency
    const formatMoneyWithCurrency = (amount, currency) => {
      if (!amount) return '';
      return `₹${Number(amount).toLocaleString('en-IN')}${currency && currency !== 'INR' ? ` ${currency}` : ''}`;
    };

    setDoiFields({
      ...emptyDoiFields,
      leadId: doiData.leadReference || '',
      leadDate: toDateInputValue(doiData.leadDate),
      leadGenerator: doiData.leadGeneratorUserId ? `${doiData.leadGeneratorUserId.firstName || ''} ${doiData.leadGeneratorUserId.lastName || ''}`.trim() : '',
      salesRep: resolveSalesRepFromDoi(doiData.salesRepUserId).displayName,
      leadSource: doiData.leadSourceId?.name || '',
      doiDate: toDateInputValue(doiData.doiDate),
      projectName: doiData.projectName || '',
      projectType: doiData.projectType || '',
      platformDomain1: doiData.platformDomains?.[0] || '',
      platformDomain2: doiData.platformDomains?.[1] || '',
      platformDomain3: doiData.platformDomains?.[2] || '',
      platformDomain4: doiData.platformDomains?.[3] || '',
      platformDomain5: doiData.platformDomains?.[4] || '',
      websiteWebServices: doiData.websiteWebServices || '',
      projectStartStage: doiData.projectStartStage || '',
      codeBase: doiData.codeBase || '',
      clientLocation: doiData.clientLocation || '',
      timeZone: doiData.timeZone || '',
      mainCustomerNames: doiData.mainCustomerNames || '',
      customerEmail: doiData.customerEmail || '',
      customerSkypeId: doiData.customerSkypeId || '',
      customerWhatsappId: doiData.customerWhatsappId || '',
      projectOrderValueRate: formatMoneyWithCurrency(doiData.projectOrderValue?.amount, doiData.projectOrderValue?.currency),
      projectWebsite: doiData.projectWebsite || '',
      estimationManHours: String(doiData.estimationManHours || ''),
      estimationApprovedBy: doiData.estimationApprovedByUsers?.map(u => `${u.firstName || ''} ${u.lastName || ''}`.trim()).filter(Boolean).join(', ') || '',
      domainWiseValueDistribution: doiData.domainWiseValueDistribution || '',
      advanceAmountReceived: formatMoneyWithCurrency(doiData.advanceAmount?.amount, doiData.advanceAmount?.currency),
      totalTimelineManDays: String(doiData.totalTimelineManDays || ''),
      detailedEstimation: doiData.detailedEstimation || '',
      projectRequirementOverview: doiData.projectRequirementOverview || '',
      sow: doiData.sow || '',
      risksAndAssumptions: doiData.risksAndAssumptions || '',
      finalDeliveryDateCommitted: toDateInputValue(doiData.finalDeliveryDate),
      milestonesSprintsDeliveryDate: formatMilestonesForField(
        Array.isArray(doiData.milestones) && doiData.milestones.length > 0
          ? doiData.milestones
          : doiData.milestonesSprintsDeliveryDate || doiData.milestonesText || doiData.milestoneSprintsDeliveryDate || ''
      )
    });

    setAttachments((doiData.attachments || []).map((attachment, index) => ({
      id: attachment._id || attachment.public_id || index,
      name: attachment.filename || `attachment-${index + 1}`,
      filename: attachment.filename,
      url: attachment.url,
      size: attachment.size || 0,
      mimeType: attachment.mimeType,
      type: attachment.mimeType,
      public_id: attachment.public_id,
      isExisting: true
    })));

    if (Array.isArray(doiData.estimationApprovedByUsers)) {
      setSelectedEstimatorIds(
        doiData.estimationApprovedByUsers
          .map((user) => user._id || user.id)
          .filter(Boolean)
      );
    }

    const { userId: salesRepUserId, displayName: salesRepName } = resolveSalesRepFromDoi(doiData.salesRepUserId);
    if (salesRepUserId) {
      setSelectedSalesRepId(salesRepUserId);
    }
    if (salesRepName) {
      setDoiFields((prev) => (prev.salesRep === salesRepName ? prev : { ...prev, salesRep: salesRepName }));
    }
  }, [doiData]);

  useEffect(() => {
    if (doiIdFromQuery) return;

    const sourceLead = leadData || (typeof opportunity?.leadId === 'object' ? opportunity.leadId : null) || opportunity || null;
    const contactPerson = customer?.contactPerson || joinName(sourceLead?.firstName, sourceLead?.lastName);
    const email = customer?.email || sourceLead?.email || '';
    const phone = getPrimaryPhone(customer, sourceLead);
    const company = customer?.companyName || sourceLead?.company || opportunity?.name || '';
    const ownerName = getBestUserName(sourceLead?.assignedTo, opportunity?.assignedTo, opportunity?.owner);
    const ownerId = sourceLead?.assignedTo?._id || opportunity?.assignedTo?._id || opportunity?.owner?._id || '';
    const createdByName = joinName(sourceLead?.createdBy?.firstName, sourceLead?.createdBy?.lastName);
    const sourceName = normalizeSourceValue(sourceLead?.source || sourceLead?.leadSource || opportunity?.source);
    const projectName = firstAvailable(sourceLead?.opportunityName, opportunity?.name, company);
    const clientLocation = getLeadLocationValue(sourceLead, customer);
    const projectValue = formatProjectValue(firstAvailable(
      sourceLead?.opportunityValue,
      sourceLead?.leadValue,
      sourceLead?.budget,
      opportunity?.amount,
      opportunity?.opportunityValue,
      opportunity?.value,
      opportunity?.totalAmount
    ));
    const requirementOverview = firstAvailable(
      sourceLead?.productRequirement,
      sourceLead?.opportunityDescription,
      sourceLead?.notes
    );
    const finalDeliveryDate = toDateInputValue(sourceLead?.opportunityExpectedCloseDate || sourceLead?.expectedCloseDate);
    const shouldFill = (currentValue) => !String(currentValue || '').trim() || isNA(currentValue);

    setDoiFields((prev) => ({
      ...prev,
      leadId: shouldFill(prev.leadId) ? (sourceLead?.leadId || opportunity?.leadId?.leadId || opportunity?.leadReference || 'N/A') : prev.leadId,
      leadDate: shouldFill(prev.leadDate) ? (toDateInputValue(sourceLead?.leadGenerationDate || opportunity?.leadGenerationDate || sourceLead?.createdAt || opportunity?.createdAt) || '') : prev.leadDate,
      leadGenerator: shouldFill(prev.leadGenerator) ? (createdByName || 'N/A') : prev.leadGenerator,
      salesRep: shouldFill(prev.salesRep) ? (ownerName || 'N/A') : prev.salesRep,
      leadSource: shouldFill(prev.leadSource) ? (sourceName || 'N/A') : prev.leadSource,
      doiDate: shouldFill(prev.doiDate) ? toDateInputValue(new Date()) : prev.doiDate,
      projectName: shouldFill(prev.projectName) ? (projectName || '') : prev.projectName,
      clientLocation: shouldFill(prev.clientLocation) ? (clientLocation || '') : prev.clientLocation,
      mainCustomerNames: shouldFill(prev.mainCustomerNames) ? (contactPerson || '') : prev.mainCustomerNames,
      customerEmail: shouldFill(prev.customerEmail) ? (email || '') : prev.customerEmail,
      customerWhatsappId: shouldFill(prev.customerWhatsappId) ? (phone || '') : prev.customerWhatsappId,
      projectOrderValueRate: shouldFill(prev.projectOrderValueRate) ? (projectValue || '') : prev.projectOrderValueRate,
      projectWebsite: shouldFill(prev.projectWebsite) ? (sourceLead?.website || customer?.website || '') : prev.projectWebsite,
      platformDomain1: shouldFill(prev.platformDomain1) ? (sourceName || '') : prev.platformDomain1,
      totalTimelineManDays: shouldFill(prev.totalTimelineManDays) ? (sourceLead?.timeline || '') : prev.totalTimelineManDays,
      projectRequirementOverview: shouldFill(prev.projectRequirementOverview) ? (requirementOverview || '') : prev.projectRequirementOverview,
      finalDeliveryDateCommitted: shouldFill(prev.finalDeliveryDateCommitted) ? (finalDeliveryDate || '') : prev.finalDeliveryDateCommitted
    }));
    if (ownerId && !selectedSalesRepId) {
      setSelectedSalesRepId(ownerId);
    }
  }, [customer, doiIdFromQuery, leadData, opportunity, selectedSalesRepId]);

  const pageError = useMemo(() => {
    if (isErrorDoi) return doiError?.message || 'Failed to load DOI data.';
    if (isErrorOpportunity) return opportunityError?.message || 'Failed to load opportunity data.';
    if (isErrorCustomer) return customerError?.message || 'Failed to load customer data.';
    return '';
  }, [customerError, doiError, isErrorCustomer, isErrorDoi, isErrorOpportunity, opportunityError]);

  const loading = isLoadingDoi || isLoadingOpportunity || isLoadingLead || isLoadingCustomer;

  const mutation = useMutation({
    mutationFn: async ({ isUpdate, formData }) => {
      if (isUpdate) return doiService.updateDoi(doiIdFromQuery, formData);
      return doiService.createDoi(formData);
    },
    onSuccess: (result, variables) => {
      if (!result.success) {
        throw new Error(result.error || (variables.isUpdate ? 'Failed to update DOI' : 'Failed to create DOI'));
      }
      toast.success(variables.isUpdate ? 'DOI updated successfully' : 'DOI created successfully');
      navigate('/crm/quotation-proposal/create-quotes');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to save DOI');
    }
  });

  const pageHeader = (
    <div className="flex items-center space-x-4">
      <button onClick={() => navigate(-1)} className="btn-cancel flex items-center space-x-2">
        <ArrowLeft className="h-4 w-4" />
        <span>Back</span>
      </button>
      <div>
        <h1 className="text-2xl font-bold text-gray-900">DOI Builder</h1>
        <p className="text-gray-600">Create and submit delivery order information</p>
      </div>
    </div>
  );

  const handleFieldChange = (key, value) => {
    const field = DOI_FIELDS.find((item) => item.key === key);
    let nextValue = value;

    if (isNumericOnlyDoiField(key)) {
      nextValue = sanitizeNumericInput(value).slice(0, MAX_FIELD_LENGTH);
    } else if (key === 'projectOrderValueRate') {
      nextValue = formatProjectValueInput(value).slice(0, MAX_FIELD_LENGTH);
    } else if (field?.type === 'email') {
      nextValue = sanitizeEmailInput(value).slice(0, 120);
    } else if (field?.multiline) {
      nextValue = sanitizeDescriptionInput(value).slice(0, MAX_TEXTAREA_LENGTH);
    } else if (field?.type !== 'date') {
      nextValue = sanitizeDescriptionInput(value).slice(0, MAX_FIELD_LENGTH);
    }

    setDoiFields((prev) => ({ ...prev, [key]: nextValue }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: '' }));
    }
  };

  const ensureAutocompleteService = () => {
    if (!autocompleteServiceRef.current && typeof window !== 'undefined' && window.google?.maps?.places) {
      autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
    }
    return autocompleteServiceRef.current;
  };

  const toggleEstimator = (user) => {
    const userId = user._id || user.id;
    if (!userId) return;

    setSelectedEstimatorIds((prev) => {
      const next = prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId];

      const selectedNames = next
        .map((id) => {
          const selectedUser = crmUsers.find((candidate) => String(candidate._id || candidate.id) === String(id));
          return getUserName(selectedUser);
        })
        .filter(Boolean);

      handleFieldChange('estimationApprovedBy', selectedNames.join(', '));
      return next;
    });
  };

  const selectedEstimators = useMemo(() => {
    return selectedEstimatorIds
      .map((id) => crmUsers.find((user) => String(user._id || user.id) === String(id)))
      .filter(Boolean);
  }, [crmUsers, selectedEstimatorIds]);

  // Update estimator display field when crmUsers list loads (for edit mode)
  useEffect(() => {
    if (crmUsers.length > 0 && selectedEstimatorIds.length > 0 && !doiFields.estimationApprovedBy) {
      const selectedNames = selectedEstimatorIds
        .map((id) => {
          const selectedUser = crmUsers.find((candidate) => String(candidate._id || candidate.id) === String(id));
          return getUserName(selectedUser);
        })
        .filter(Boolean);

      if (selectedNames.length > 0) {
        setDoiFields(prev => ({ ...prev, estimationApprovedBy: selectedNames.join(', ') }));
      }
    }
  }, [crmUsers, selectedEstimatorIds]);

  // Update sales rep display field when salesRepUsers list loads (for edit mode)
  useEffect(() => {
    if (salesRepUsers.length > 0 && selectedSalesRepId) {
      const selectedUser = salesRepUsers.find((user) => isSameSalesRep(user, selectedSalesRepId));
      if (selectedUser && doiFields.salesRep !== getUserName(selectedUser)) {
        setDoiFields(prev => ({ ...prev, salesRep: getUserName(selectedUser) }));
      }
    }
  }, [doiFields.salesRep, salesRepUsers, selectedSalesRepId]);

  const filteredTimeZones = useMemo(() => {
    const search = normalizeSearchText(timeZoneSearch);
    if (!search) return TIMEZONE_OPTIONS;
    return TIMEZONE_OPTIONS.filter((timeZone) => (
      getTimeZoneSearchValues(timeZone).some((value) => value.includes(search))
    ));
  }, [timeZoneSearch]);

  const handleSalesRepSelect = (user) => {
    const userId = getSalesRepUserId(user);
    if (!userId) return;
    setSelectedSalesRepId(String(userId));
    handleFieldChange('salesRep', user.name || getUserName(user));
    setIsSalesRepDropdownOpen(false);
  };

  const handleLocationInputChange = (value) => {
    handleFieldChange('clientLocation', value);

    const autocompleteService = ensureAutocompleteService();
    if (!value || value.trim().length < 2 || !autocompleteService) {
      setLocationPredictions([]);
      setShowLocationPredictions(false);
      return;
    }

    const requestSeq = locationRequestSeqRef.current + 1;
    locationRequestSeqRef.current = requestSeq;

    autocompleteService.getPlacePredictions({
      input: value,
      types: ['geocode', 'establishment']
    }, (results, status) => {
      if (requestSeq !== locationRequestSeqRef.current) return;
      if (status === window.google?.maps?.places?.PlacesServiceStatus?.OK && results) {
        setLocationPredictions(results);
        setShowLocationPredictions(true);
      } else {
        setLocationPredictions([]);
        setShowLocationPredictions(false);
      }
    });
  };

  const handleLocationSelect = (prediction) => {
    handleFieldChange('clientLocation', cleanLocationValue(prediction.description));
    setLocationPredictions([]);
    setShowLocationPredictions(false);
  };

  const validateField = (field) => {
    const value = String(doiFields[field.key] || '').trim();
    if (!value) {
      return isRequiredDoiField(field.key) ? 'Lead ID is required.' : '';
    }

    if (field.type === 'email' && !isNA(value)) {
      const validation = validateEmailInput(value);
      if (!validation.isValid) return validation.error;
    }

    if (field.key === 'customerWhatsappId' && !isNA(value)) {
      const validation = validatePhoneInput(value);
      if (!validation.isValid) return validation.error;
    }

    if (isNumericOnlyDoiField(field.key) && !/^\d+$/.test(value)) {
      return 'Only numeric values are allowed.';
    }

    if (['projectWebsite', 'websiteWebServices'].includes(field.key) && field.key === 'projectWebsite' && !isValidUrlOrNA(value)) {
      return 'Please enter a valid website URL.';
    }

    if (['leadDate', 'doiDate', 'finalDeliveryDateCommitted'].includes(field.key)) {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return 'Please enter a valid date.';
    }

    if (field.key === 'leadDate' && value && doiFields.doiDate) {
      if (new Date(value) > new Date(doiFields.doiDate)) {
        return 'Lead date cannot be after DOI date.';
      }
    }

    if (field.key === 'finalDeliveryDateCommitted' && value && doiFields.doiDate) {
      if (new Date(value) < new Date(doiFields.doiDate)) {
        return 'Delivery date cannot be before DOI date.';
      }
    }

    return '';
  };

  const validateForm = () => {
    const nextErrors = {};
    DOI_FIELDS.forEach((field) => {
      const fieldError = validateField(field);
      if (fieldError) nextErrors[field.key] = fieldError;
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const processFiles = async (files) => {
    const MAX_FILE_SIZE = 25 * 1024 * 1024;
    if (!files.length) return;

    setIsUploadingAttachment(true);
    try {
      for (const file of files) {
        if (file.size > MAX_FILE_SIZE) {
          toast.error(`File ${file.name} exceeds 25MB limit`);
          continue;
        }

        setUploadProgress(0);
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);
        uploadFormData.append('folder', 'dois');

        const progressInterval = setInterval(() => {
          setUploadProgress((prev) => (prev >= 90 ? prev : prev + 10));
        }, 500);

        const result = await post(API_ENDPOINTS.UPLOAD.CLOUDINARY, uploadFormData);
        clearInterval(progressInterval);
        setUploadProgress(100);

        if (result.responseCode === 200) {
          setAttachments((prev) => [
            ...prev,
            {
              id: result.data.public_id || Date.now() + Math.random(),
              name: file.name,
              filename: file.name,
              size: file.size,
              mimeType: file.type,
              type: file.type,
              url: result.data.url,
              public_id: result.data.public_id,
              isExisting: false
            }
          ]);
          toast.success(`File ${file.name} uploaded successfully`);
        } else {
          toast.error(result.responseMessage || `Failed to upload ${file.name}`);
        }

        setTimeout(() => setUploadProgress(0), 1000);
      }
    } catch (error) {
      toast.error('An error occurred during file upload');
      setUploadProgress(0);
    } finally {
      setIsUploadingAttachment(false);
    }
  };

  const handleFileUpload = async (event) => {
    await processFiles(Array.from(event.target.files || []));
    event.target.value = '';
  };

  const removeAttachment = async (index) => {
    const attachment = attachments[index];
    if (!attachment.isExisting && attachment.public_id) {
      try {
        await del(`${API_ENDPOINTS.UPLOAD.CLOUDINARY}?public_id=${attachment.public_id}&resource_type=raw`);
      } catch (error) {
        // Keep UI responsive even if storage cleanup fails.
      }
    }
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const triggerDownload = async (attachment) => {
    if (!attachment?.url) {
      toast.error('No attachment URL found');
      return;
    }

    const token = localStorage.getItem('auth_token');
    const filename = attachment.filename || attachment.name || 'attachment';
    try {
      const response = await fetch(
        `${API_BASE_URL}/upload/cloudinary/download?public_id=${encodeURIComponent(attachment.public_id || '')}&filename=${encodeURIComponent(filename)}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      if (!response.ok) throw new Error('Failed to fetch attachment');
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {
      window.open(attachment.url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      toast.error('Please enter Lead ID.');
      return;
    }

    const formData = new FormData();
    const leadObjectId = opportunity?.leadId?._id || leadData?._id || opportunity?._id || null;
    const leadReference = opportunity?.leadId?.leadId || leadData?.leadId || doiFields.leadId || null;
    const customerObjectId = customer?._id || null;

    // Extract leadGeneratorUserId from lead/opportunity data
    const leadGeneratorId = leadData?.createdBy?._id ||
      opportunity?.createdBy?._id ||
      (typeof leadData?.createdBy === 'string' ? leadData.createdBy : null) ||
      (typeof opportunity?.createdBy === 'string' ? opportunity.createdBy : null) ||
      null;

    const resolveLeadSourceId = () => {
      const objectIdCandidates = [
        leadData?.source?._id,
        leadData?.leadSource?._id,
        opportunity?.source?._id
      ];

      for (const candidate of objectIdCandidates) {
        if (candidate && isValidObjectId(String(candidate))) {
          return String(candidate);
        }
      }

      const nameCandidates = [
        typeof leadData?.source === 'object' ? leadData?.source?.name : leadData?.source,
        typeof leadData?.leadSource === 'object' ? leadData?.leadSource?.name : leadData?.leadSource,
        typeof opportunity?.source === 'object' ? opportunity?.source?.name : opportunity?.source,
        doiFields.leadSource
      ]
        .map((value) => String(value || '').trim())
        .filter(Boolean);

      for (const name of nameCandidates) {
        if (isValidObjectId(name)) return name;

        const matchedSource = leadSources.find((source) => (
          String(source._id || source.id) === name
          || String(source.name || '').toLowerCase() === name.toLowerCase()
          || String(source.leadSourceCode || '').toLowerCase() === name.toLowerCase()
        ));

        if (matchedSource?._id || matchedSource?.id) {
          return String(matchedSource._id || matchedSource.id);
        }
      }

      return null;
    };

    const leadSourceId = resolveLeadSourceId();

    // Add references - ensure single values, not arrays
    if (opportunity?._id) formData.append('opportunityId', opportunity._id);
    if (leadObjectId) {
      // Ensure leadId is a single ObjectId, not an array
      const singleLeadId = Array.isArray(leadObjectId) ? leadObjectId[0] : leadObjectId;
      formData.append('leadId', singleLeadId);
    }
    if (leadReference) {
      // Ensure leadReference is a single string, not an array
      const singleLeadRef = Array.isArray(leadReference) ? leadReference[0] : leadReference;
      formData.append('leadReference', singleLeadRef);
    }
    if (customerObjectId) formData.append('customerId', customerObjectId);
    if (selectedSalesRepId) formData.append('salesRepUserId', selectedSalesRepId);
    if (leadGeneratorId) formData.append('leadGeneratorUserId', leadGeneratorId);
    if (leadSourceId) formData.append('leadSourceId', leadSourceId);

    // Fields to exclude from doiFields (already handled separately or need special processing)
    const excludedFields = ['leadId', 'leadGenerator', 'salesRep', 'leadSource', 'estimationApprovedBy',
      'platformDomain1', 'platformDomain2', 'platformDomain3', 'platformDomain4', 'platformDomain5',
      'projectOrderValueRate', 'advanceAmountReceived', 'finalDeliveryDateCommitted',
      'milestonesSprintsDeliveryDate'];

    // Add all DOI fields at root level (not inside 'fields' object)
    Object.keys(doiFields).forEach(key => {
      // Skip fields that are handled separately
      if (excludedFields.includes(key)) return;

      const value = doiFields[key];
      if (value !== null && value !== undefined && value !== '' && !isNA(value)) {
        formData.append(key, isNumericOnlyDoiField(key) ? sanitizeNumericInput(value) : value);
      }
    });

    // Handle platform domains - convert platformDomain1-5 to platformDomains array
    const platformDomains = [
      doiFields.platformDomain1,
      doiFields.platformDomain2,
      doiFields.platformDomain3,
      doiFields.platformDomain4,
      doiFields.platformDomain5
    ].filter((value) => value && !isNA(value));
    if (platformDomains.length > 0) {
      formData.append('platformDomains', JSON.stringify(platformDomains));
    }

    // Handle projectOrderValue - parse from formatted string
    if (doiFields.projectOrderValueRate) {
      const valueMatch = String(doiFields.projectOrderValueRate).match(/[\d,]+\.?\d*/);
      const currencyMatch = String(doiFields.projectOrderValueRate).match(/[A-Z]{3}/);
      const amount = valueMatch ? parseFloat(valueMatch[0].replace(/,/g, '')) : 0;
      const currency = currencyMatch ? currencyMatch[0] : 'INR';
      formData.append('projectOrderValue', JSON.stringify({ amount, currency }));
    }

    // Handle advanceAmount - parse from formatted string
    if (doiFields.advanceAmountReceived) {
      const valueMatch = String(doiFields.advanceAmountReceived).match(/[\d,]+\.?\d*/);
      const currencyMatch = String(doiFields.advanceAmountReceived).match(/[A-Z]{3}/);
      const amount = valueMatch ? parseFloat(valueMatch[0].replace(/,/g, '')) : 0;
      const currency = currencyMatch ? currencyMatch[0] : 'INR';
      formData.append('advanceAmount', JSON.stringify({ amount, currency }));
    }

    // Handle finalDeliveryDate
    if (doiFields.finalDeliveryDateCommitted) {
      formData.append('finalDeliveryDate', doiFields.finalDeliveryDateCommitted);
    }

    const milestonesText = String(doiFields.milestonesSprintsDeliveryDate || '').trim();
    if (milestonesText) {
      formData.append('milestonesSprintsDeliveryDate', milestonesText);
      formData.append('milestones', JSON.stringify(parseMilestonesFromField(milestonesText)));
    }

    // Add estimation approved by users
    formData.append('estimationApprovedByUsers', JSON.stringify(selectedEstimatorIds));

    // Add attachments
    formData.append('attachments', JSON.stringify(attachments.map((attachment) => ({
      url: attachment.url,
      public_id: attachment.public_id,
      mimeType: attachment.mimeType || attachment.type,
      size: attachment.size,
      filename: attachment.filename || attachment.name
    }))));

    mutation.mutate({ isUpdate: !!doiIdFromQuery, formData });
  };

  const isSubmitDisabled = mutation.isPending || isUploadingAttachment || !String(doiFields.leadId || '').trim();

  const renderFieldControl = (field) => {
    if (field.key === 'salesRep') {
      return (
        <div className="relative" ref={salesRepDropdownRef}>
          <button
            type="button"
            onClick={() => setIsSalesRepDropdownOpen((prev) => !prev)}
            className={`input-field text-left min-h-[42px] flex items-center justify-between gap-2 ${errors.salesRep ? 'border-red-300' : ''}`}
          >
            <span className={doiFields.salesRep ? 'truncate text-gray-900' : 'truncate text-gray-400'}>
              {doiFields.salesRep || FIELD_PLACEHOLDERS.salesRep}
            </span>
            <ChevronDown className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${isSalesRepDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
          {isSalesRepDropdownOpen && (
            <div className="absolute z-50 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg">
              <div className="p-2 border-b border-gray-100">
                <input
                  type="text"
                  value={salesRepSearch}
                  onChange={(event) => setSalesRepSearch(sanitizeDescriptionInput(event.target.value).slice(0, 80))}
                  className="input-field"
                  placeholder="Search sales reps..."
                />
              </div>
              <div className="max-h-64 overflow-y-auto">
                {isLoadingSalesRepUsers ? (
                  <div className="px-3 py-3 text-sm text-gray-500">Loading sales reps...</div>
                ) : salesRepUsers.length === 0 ? (
                  <div className="px-3 py-3 text-sm text-gray-500">No sales reps found</div>
                ) : (
                  salesRepUsers.map((user) => {
                    const userId = getSalesRepUserId(user);
                    const checked = isSameSalesRep(user, selectedSalesRepId);
                    return (
                      <button
                        key={userId}
                        type="button"
                        onClick={() => handleSalesRepSelect(user)}
                        className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-start gap-3"
                      >
                        <input type="radio" checked={checked} readOnly className="mt-1" />
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-gray-900">{getUserName(user)}</span>
                          <span className="block text-xs text-gray-500 truncate">{user.email || user.role || 'CRM user'}</span>
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
      );
    }

    if (field.key === 'leadSource') {
      const currentLeadSource = String(doiFields.leadSource || '').trim();
      const hasCurrentLeadSourceOption = leadSources.some((source) => (
        String(source.name || '').toLowerCase() === currentLeadSource.toLowerCase()
      ));

      return (
        <select
          value={doiFields.leadSource}
          onChange={(event) => handleFieldChange('leadSource', event.target.value)}
          className={`input-field ${errors.leadSource ? 'border-red-300' : ''}`}
          required={isRequiredDoiField(field.key)}
        >
          <option value="">{FIELD_PLACEHOLDERS.leadSource}</option>
          {currentLeadSource && !hasCurrentLeadSourceOption && (
            <option value={currentLeadSource}>{currentLeadSource}</option>
          )}
          {leadSources.map((source) => (
            <option key={source._id || source.id || source.name} value={source.name} className="capitalize">
              {source.name}
            </option>
          ))}
        </select>
      );
    }

    if (field.control === 'projectType') {
      return (
        <select
          value={doiFields.projectType}
          onChange={(event) => handleFieldChange('projectType', event.target.value)}
          className={`input-field ${errors.projectType ? 'border-red-300' : ''}`}
          required={isRequiredDoiField(field.key)}
        >
          <option value="">{FIELD_PLACEHOLDERS.projectType}</option>
          {PROJECT_TYPE_OPTIONS.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }

    if (field.control === 'websiteWebServices') {
      return (
        <select
          value={doiFields.websiteWebServices}
          onChange={(event) => handleFieldChange('websiteWebServices', event.target.value)}
          className={`input-field ${errors.websiteWebServices ? 'border-red-300' : ''}`}
          required={isRequiredDoiField(field.key)}
        >
          <option value="">{FIELD_PLACEHOLDERS.websiteWebServices}</option>
          {WEBSITE_WEB_SERVICES_OPTIONS.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }

    if (field.control === 'projectStartStage') {
      return (
        <select
          value={doiFields.projectStartStage}
          onChange={(event) => handleFieldChange('projectStartStage', event.target.value)}
          className={`input-field ${errors.projectStartStage ? 'border-red-300' : ''}`}
          required={isRequiredDoiField(field.key)}
        >
          <option value="">{FIELD_PLACEHOLDERS.projectStartStage}</option>
          {PROJECT_START_STAGE_OPTIONS.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }

    if (field.control === 'codeBase') {
      return (
        <select
          value={doiFields.codeBase}
          onChange={(event) => handleFieldChange('codeBase', event.target.value)}
          className={`input-field ${errors.codeBase ? 'border-red-300' : ''}`}
          required={isRequiredDoiField(field.key)}
        >
          <option value="">{FIELD_PLACEHOLDERS.codeBase}</option>
          {CODE_BASE_OPTIONS.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }

    if (field.control === 'estimationApprovedBy') {
      return (
        <div className="relative" ref={estimatorDropdownRef}>
          <button
            type="button"
            onClick={() => setIsEstimatorDropdownOpen((prev) => !prev)}
            className={`input-field text-left min-h-[42px] ${errors.estimationApprovedBy ? 'border-red-300' : ''}`}
          >
            {doiFields.estimationApprovedBy || FIELD_PLACEHOLDERS.estimationApprovedBy}
          </button>
          {isEstimatorDropdownOpen && (
            <div className="absolute z-50 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg">
              <div className="p-2 border-b border-gray-100">
                <input
                  type="text"
                  value={estimatorSearch}
                  onChange={(event) => setEstimatorSearch(sanitizeDescriptionInput(event.target.value).slice(0, 80))}
                  className="input-field"
                  placeholder="Search users..."
                />
              </div>
              <div className="max-h-64 overflow-y-auto">
                {isLoadingCrmUsers ? (
                  <div className="px-3 py-3 text-sm text-gray-500">Loading users...</div>
                ) : crmUsers.length === 0 ? (
                  <div className="px-3 py-3 text-sm text-gray-500">No users found</div>
                ) : (
                  crmUsers.map((user) => {
                    const userId = user._id || user.id;
                    const checked = selectedEstimatorIds.includes(userId);
                    return (
                      <button
                        key={userId}
                        type="button"
                        onClick={() => toggleEstimator(user)}
                        className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-start gap-3"
                      >
                        <input type="checkbox" checked={checked} readOnly className="mt-1" />
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-gray-900">{getUserName(user)}</span>
                          <span className="block text-xs text-gray-500 truncate">{user.email || user.role || 'CRM user'}</span>
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
              {selectedEstimators.length > 0 && (
                <div className="border-t border-gray-100 p-2">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedEstimatorIds([]);
                      handleFieldChange('estimationApprovedBy', '');
                    }}
                    className="text-xs text-red-600 hover:text-red-700"
                  >
                    Clear selection
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    if (field.key === 'timeZone') {
      const currentTimeZone = String(doiFields.timeZone || '').trim();
      const hasCurrentTimeZoneOption = TIMEZONE_OPTIONS.includes(currentTimeZone);

      return (
        <div className="relative" ref={timeZoneDropdownRef}>
          <button
            type="button"
            onClick={() => setIsTimeZoneDropdownOpen((prev) => !prev)}
            className={`input-field text-left min-h-[42px] ${errors.timeZone ? 'border-red-300' : ''}`}
          >
            {doiFields.timeZone ? getTimeZoneDisplayLabel(doiFields.timeZone) : FIELD_PLACEHOLDERS.timeZone}
          </button>
          {isTimeZoneDropdownOpen && (
            <div className="absolute z-50 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg">
              <div className="p-2 border-b border-gray-100">
                <input
                  type="text"
                  value={timeZoneSearch}
                  onChange={(event) => setTimeZoneSearch(sanitizeDescriptionInput(event.target.value).slice(0, 80))}
                  className="input-field"
                  placeholder="Search timezone..."
                />
              </div>
              <div className="max-h-64 overflow-y-auto">
                {currentTimeZone && !hasCurrentTimeZoneOption && (
                  <button
                    type="button"
                    onClick={() => {
                      handleFieldChange('timeZone', currentTimeZone);
                      setIsTimeZoneDropdownOpen(false);
                    }}
                    className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                  >
                    {getTimeZoneDisplayLabel(currentTimeZone)}
                  </button>
                )}
                {filteredTimeZones.length === 0 ? (
                  <div className="px-3 py-3 text-sm text-gray-500">No timezones found</div>
                ) : (
                  filteredTimeZones.map((timeZone) => (
                    <button
                      key={timeZone}
                      type="button"
                      onClick={() => {
                        handleFieldChange('timeZone', timeZone);
                        setIsTimeZoneDropdownOpen(false);
                        setTimeZoneSearch('');
                      }}
                      className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                    >
                      {getTimeZoneDisplayLabel(timeZone)}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      );
    }

    if (field.key === 'clientLocation') {
      return (
        <div className="relative" ref={locationDropdownRef}>
          <input
            type="text"
            value={doiFields.clientLocation}
            onChange={(event) => handleLocationInputChange(event.target.value)}
            onBlur={() => {
              const fieldError = validateField(field);
              if (fieldError) setErrors((prev) => ({ ...prev, [field.key]: fieldError }));
            }}
            className={`input-field pr-10 ${errors.clientLocation ? 'border-red-300' : ''}`}
            placeholder={FIELD_PLACEHOLDERS.clientLocation}
            maxLength={MAX_FIELD_LENGTH}
            autoComplete="off"
            required={isRequiredDoiField(field.key)}
          />
          {typeof window !== 'undefined' && window.google?.maps?.places && (
            <MapPin className="absolute right-3 top-[21px] -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          )}
          {showLocationPredictions && locationPredictions.length > 0 && (
            <div className="absolute z-50 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg max-h-60 overflow-y-auto">
              {locationPredictions.map((prediction) => (
                <button
                  key={prediction.place_id}
                  type="button"
                  onClick={() => handleLocationSelect(prediction)}
                  className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-start gap-2"
                >
                  <MapPin className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <span>{prediction.description}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (field.multiline) {
      return (
        <textarea
          rows={3}
          value={doiFields[field.key]}
          placeholder={FIELD_PLACEHOLDERS[field.key]}
          onChange={(event) => handleFieldChange(field.key, event.target.value)}
          onBlur={() => {
            const fieldError = validateField(field);
            if (fieldError) setErrors((prev) => ({ ...prev, [field.key]: fieldError }));
          }}
          className={`input-field min-h-[96px] resize-y ${errors[field.key] ? 'border-red-300' : ''}`}
          maxLength={MAX_TEXTAREA_LENGTH}
          required={isRequiredDoiField(field.key)}
        />
      );
    }

    const isReadOnlyLeadId = field.key === 'leadId' && isLeadIdLocked;
    const isNumericOnly = isNumericOnlyDoiField(field.key);

    return (
      <input
        type={field.type || 'text'}
        value={doiFields[field.key]}
        placeholder={FIELD_PLACEHOLDERS[field.key]}
        onChange={(event) => {
          if (isReadOnlyLeadId) return;
          handleFieldChange(field.key, event.target.value);
        }}
        onBlur={() => {
          const fieldError = validateField(field);
          if (fieldError) setErrors((prev) => ({ ...prev, [field.key]: fieldError }));
        }}
        className={`input-field ${isReadOnlyLeadId ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''} ${errors[field.key] ? 'border-red-300' : ''}`}
        maxLength={field.type === 'date' ? undefined : MAX_FIELD_LENGTH}
        inputMode={isNumericOnly ? 'numeric' : undefined}
        pattern={isNumericOnly ? '[0-9]*' : undefined}
        max={field.key === 'leadDate' ? (doiFields.doiDate || undefined) : undefined}
        min={field.key === 'finalDeliveryDateCommitted' ? (doiFields.doiDate || undefined) : undefined}
        readOnly={isReadOnlyLeadId}
        required={isRequiredDoiField(field.key)}
      />
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {pageHeader}
        <div className="bg-white border border-gray-200 rounded-lg p-12">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-200 rounded w-48 mx-auto" />
            <div className="h-4 bg-gray-200 rounded w-32 mx-auto" />
            <div className="space-y-3 mt-8">
              <div className="h-4 bg-gray-200 rounded w-full" />
              <div className="h-4 bg-gray-200 rounded w-5/6" />
              <div className="h-4 bg-gray-200 rounded w-4/6" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (pageError) {
    return (
      <div className="space-y-6">
        {pageHeader}
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800">Error Loading Data</h3>
              <p className="text-sm text-red-700 mt-1">{pageError}</p>
              <button
                className="btn-cancel mt-4"
                onClick={() => {
                  if (finalOpportunityId) refetchOpportunity();
                  if (linkedCustomerId) refetchCustomer();
                }}
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        {pageHeader}
      </div>

      {(opportunity || customer) && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {customer ? 'Customer Information' : 'Opportunity Information'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start space-x-3">
              <Building className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-gray-600 mb-1">Company / Project</p>
                <p className="font-medium text-gray-900 break-words">
                  {customer?.companyName || leadData?.company || opportunity?.leadId?.company || opportunity?.name || 'N/A'}
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <User className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-gray-600 mb-1">Customer Contact</p>
                <p className="font-medium text-gray-900 break-words">
                  {customer?.contactPerson || doiFields.mainCustomerNames || 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">DOI Form</h3>
            <p className="text-sm text-gray-600 mt-1">Only Lead ID is mandatory. All other DOI details can be added when available.</p>
          </div>
          {doiData?.doiNumber && (
            <span className="text-sm font-medium text-gray-700 bg-gray-100 rounded px-3 py-1">
              {doiData.doiNumber}
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {DOI_FIELDS.map((field, index) => (
            <div key={field.key} className={`${field.span === 2 ? 'md:col-span-2' : ''} flex flex-col`}>
              <label className="block text-sm font-medium text-gray-700 mb-2 md:min-h-[40px]">
                <span className="text-gray-500 mr-1">{index + 1}.</span>
                {field.label} {isRequiredDoiField(field.key) && <span className="text-red-500">*</span>}
              </label>
              {renderFieldControl(field)}
              {errors[field.key] && (
                <p className="text-xs text-red-600 mt-1">{errors[field.key]}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      <div
        className={`bg-white border rounded-lg p-6 transition-colors ${isDragging ? 'border-brand bg-green-50' : 'border-gray-200'}`}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          if (!event.currentTarget.contains(event.relatedTarget)) setIsDragging(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setIsDragging(false);
          processFiles(Array.from(event.dataTransfer.files || []));
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Attachments</h3>
          <PermissionGate permission="quotation_management" action="update">
            <button
              type="button"
              onClick={() => document.getElementById('doiFileInput').click()}
              className="btn-create flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>Add Attachment</span>
            </button>
          </PermissionGate>
        </div>

        <input
          id="doiFileInput"
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xlsx,.xls"
          onChange={handleFileUpload}
          className="hidden"
        />

        {isUploadingAttachment && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-blue-800 font-medium">Uploading attachment... {uploadProgress}%</span>
              <Loader2 className="h-5 w-5 text-[#0F2942] animate-spin" />
            </div>
            <div className="w-full bg-blue-200 rounded-full h-2.5">
              <div className="bg-[#0F2942] h-2.5 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
            </div>
          </div>
        )}

        {attachments.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {attachments.map((attachment, index) => (
              <div key={attachment.id || index} className="border border-gray-200 rounded-lg p-4 flex items-start justify-between">
                <div className="flex items-start space-x-3 min-w-0">
                  <FileText className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 break-words">
                      {attachment.name || attachment.filename}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {attachment.size ? `${(attachment.size / 1024).toFixed(1)} KB` : 'Uploaded file'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 ml-2">
                  {attachment.url && (
                    <button type="button" onClick={() => triggerDownload(attachment)} className="text-gray-400 hover:text-blue-500" title="Download">
                      <Download className="h-4 w-4" />
                    </button>
                  )}
                  <button type="button" onClick={() => removeAttachment(index)} className="text-gray-400 hover:text-red-500" title="Remove">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-10 text-center">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {isDragging ? 'Drop files here' : 'No attachments'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">Upload DOI support documents when available.</p>
          </div>
        )}
      </div>

      <div className="flex justify-center mt-6">
        <PermissionGate permission="quotation_management" action={doiIdFromQuery ? 'update' : 'create'}>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            title={isSubmitDisabled ? 'Please enter Lead ID' : ''}
            className={`btn-create flex items-center space-x-2 ${isSubmitDisabled ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            <Save className="h-4 w-4" />
            <span>
              {mutation.isPending
                ? (doiIdFromQuery ? 'Updating...' : 'Creating...')
                : (doiIdFromQuery ? 'Update DOI' : 'Create DOI')}
            </span>
          </button>
        </PermissionGate>
      </div>
    </div>
  );
};

export default QuoteBuilder;
